// Members' own storage and libraries (docs/STORAGE.md §2.5): what the app offers, worked out
// from what the server reports.
import type { LocalUser, MyStorage, StorageLocation, UserProfile } from '../api/types'

/** A storage location a new library can go in, as the picker names it. */
export interface LibraryPlace {
  id: string
  label: string
}

/** Where I may create a library: my own buckets and nodes, then other people's nodes open to me. */
export function libraryPlaces(mine: readonly StorageLocation[], opened: readonly OpenPlace[] = []): LibraryPlace[] {
  return [
    ...mine.map((l) => ({ id: l.id, label: `${l.name} · yours` })),
    ...opened.map((l) => ({ id: l.id, label: `${l.name} · ${l.owner}'s node · ${l.room}` })),
  ]
}

/** Someone's node open to me, as its answer named it, with my room there. */
export interface OpenPlace {
  id: string
  name: string
  owner: string
  room: string
}

/** A size in MiB as typed in GB ("" for no limit). */
export const gbText = (mb: number | null | undefined) => (mb == null ? '' : String(Math.round((mb / 1024) * 100) / 100))

/** GB as typed into MiB: blank is no limit (null); anything not a number of 0 or more is undefined. */
export function mbFromGb(text: string): number | null | undefined {
  const t = text.trim()
  if (!t) return null
  const gb = Number(t)
  return Number.isFinite(gb) && gb >= 0 ? Math.round(gb * 1024) : undefined
}

/**
 * Who the owner of a library may share it with, when they aren't an admin: the people on
 * this server they share a space or conversation with (members get no list of the server's
 * accounts; the server checks the same).
 */
export function grantees(profiles: Iterable<UserProfile>, me: string | null): LocalUser[] {
  const domain = me?.split('@')[1]
  if (!domain) return []
  return [...profiles]
    .filter((p) => p.user_id !== me && p.user_id.split('@')[1] === domain)
    .map((p) => ({ user_id: p.user_id, display_name: p.display_name, is_admin: false, can_create_spaces: false, spaces_owned: 0 }))
    .sort((a, b) => a.display_name.localeCompare(b.display_name))
}

/** Where a location keeps its files: a folder's path, bucket/prefix · endpoint · region, or a node's. */
export function whereIs(l: StorageLocation): string {
  if (l.kind === 'disk') return l.path ?? ''
  if (l.kind === 'node') return 'a computer of its owner’s, serving through this server'
  const bucket = [l.bucket, l.prefix].filter(Boolean).join('/')
  return `${bucket} · ${l.endpoint || 'AWS'}${l.region ? ` · ${l.region}` : ''}`
}

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/** What moving my account to another server would remove here, or null if nothing. */
export function moveWarning(mine: Pick<MyStorage, 'libraries' | 'locations'>): string | null {
  const parts = [
    mine.libraries ? count(mine.libraries, 'library', 'libraries') : '',
    mine.locations.length ? count(mine.locations.length, 'storage location', 'storage locations') : '',
  ].filter(Boolean)
  if (!parts.length) return null
  return (
    `Moving removes your ${parts.join(' and ')} from this server: libraries don't move between servers. ` +
    `The files stay in your storage, but without the library's index they can't be opened again.`
  )
}

/** A word for a POSIX shell, single-quoted. */
export const shellQuote = (s: string) => `'${s.replaceAll("'", `'\\''`)}'`

/**
 * The command that makes a computer a node and shares a folder from it (docs/STORAGE.md §11.3),
 * the same on a NAS, a desktop or the server's own machine: run in the folder to share.
 */
export const nodeCommand = (link: string) => `hrth node setup --link ${shellQuote(link)}`

/** Installs the hrth command line from this server (`server`: its URL). */
export const installCommand = (server: string) => `curl -fsSL ${shellQuote(`${server}/install.sh`)} | sh`
